/**
 * Recargas de saldo.
 *
 * A top-up is commission income, not a product sale. Booking C$100 of phone credit as revenue
 * would inflate sales by the full face value and make every margin report meaningless — the
 * shop earned five córdobas, not a hundred.
 *
 * Credit is bought in bulk below face and carried as an asset at cost, averaged exactly the way
 * inventory is. The margin is realised when a recarga is sold at face.
 */

import {
  ZERO,
  addMoney,
  centavos,
  fechaEnNicaragua,
  mulDiv,
  orderKey,
  subMoney,
  type AnyDomainEvent,
  type Centavos,
  type CuentaBanco,
} from '@abastece/shared';
import { credit, debit, entry } from '../ledger/journal.js';
import type { AccountKey } from '../ledger/accounts.js';
import { saldoId, type ApplyResult, type Effect, type Reader } from '../state.js';
import { cuentaCerradaParaSiempre, cuentaDeTransferencia } from './bancos.js';
import { diaDeLaConfirmacion } from './contadas.js';

/**
 * Where a top-up's money lands once it is certain: cash in the drawer at once; a transfer or a
 * wallet payment only when somebody has seen it arrive, and until then in the same holding
 * account as a sale's (`TRANSFERENCIAS_PENDIENTES`), in the same queue. Before, a top-up paid by
 * transfer went straight to the bank on a screenshot — the one payment in the shop nobody checked.
 */
const CUENTA_CONFIRMADA: Record<string, AccountKey> = {
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

/** Buying credit in bulk is a payment out, so it comes from general cash, not the drawer. */
const CUENTA_COMPRA: Record<string, AccountKey> = {
  efectivo_cordobas: 'CAJA_FUERTE',
  transferencia: 'BANCOS',
  billetera: 'BILLETERA_MOVIL',
};

export async function reduceRecarga(
  event: AnyDomainEvent,
  read: Reader,
): Promise<ApplyResult | null> {
  const rev = orderKey(event);

  switch (event.type) {
    case 'recarga.saldo_comprado': {
      const p = event.payload;
      const id = saldoId(p.operadora);
      const actual = await read.saldoTelefonico(id);
      const cerrada = cuentaCerradaParaSiempre(await read.config(), p.cuenta);
      if (cerrada) return { effects: [], rejections: [{ eventId: event.id, reason: cerrada }] };

      const facial = addMoney(actual?.facial ?? ZERO, centavos(p.montoFacial));
      const costo = addMoney(actual?.costo ?? ZERO, centavos(p.costo));

      return {
        effects: [
          {
            kind: 'saldo.upsert',
            id,
            row: { id, operadora: p.operadora, facial, costo, rev },
          },
          {
            kind: 'asiento',
            entry: entry(
              event.id,
              fechaEnNicaragua(event.clientTs),
              `Compra de saldo ${p.operadora}`,
              [
                debit('SALDO_TELEFONICO', centavos(p.costo), `Facial ${p.montoFacial / 100}`),
                credit(
                  p.formaPago === 'transferencia' ? cuentaDeTransferencia(p.cuenta) : (CUENTA_COMPRA[p.formaPago] ?? 'CAJA_FUERTE'),
                  centavos(p.costo),
                ),
              ],
            ),
          },
        ],
        rejections: [],
      };
    }

    case 'recarga.saldo_contado': {
      const p = event.payload;
      const id = saldoId(p.operadora);
      const actual = await read.saldoTelefonico(id);
      const libros = actual?.facial ?? ZERO;
      const real = centavos(p.facial);
      const diferencia = subMoney(real, libros);

      /*
       * Valued at what the balance cost on average. With nothing on record to average — never bought,
       * or sold past what there was, so the books went below zero — what was counted is taken at
       * face, and what the books had moves to that: averaged from a balance below zero, the cost
       * came out above face, and every top-up after it cost more than it charged, a commission
       * below nothing that the entry could not hold («Asiento descuadrado»), refused for good.
       * Credit that cost nothing — an operator's bonus, bought at 0 — is a basis too: it used to be
       * taken at face, and a count that matched booked its whole face as found (2026-10-20).
       */
      const hayBase = actual && actual.facial > 0 && actual.costo >= 0;
      const costo = hayBase
        ? diferencia < 0
          ? subMoney(actual.costo, mulDiv(actual.costo, centavos(-diferencia), actual.facial))
          : addMoney(actual.costo, mulDiv(actual.costo, diferencia, actual.facial))
        : real;
      const cambio = subMoney(costo, actual?.costo ?? ZERO);
      const valor = centavos(Math.abs(cambio));

      const effects: Effect[] = [
        { kind: 'saldo.upsert', id, row: { id, operadora: actual?.operadora ?? p.operadora, facial: real, costo, rev } },
      ];
      if (valor > 0) {
        effects.push({
          kind: 'asiento',
          entry: entry(
            event.id,
            fechaEnNicaragua(event.clientTs),
            `Conteo de saldo ${p.operadora}${p.nota ? `: ${p.nota}` : ''}`,
            cambio < 0
              ? [debit('FALTANTE_INVENTARIO', valor, `Faltan ${-diferencia / 100} de saldo`), credit('SALDO_TELEFONICO', valor)]
              : [debit('SALDO_TELEFONICO', valor), credit('SOBRANTE_INVENTARIO', valor, `Sobran ${diferencia / 100} de saldo`)],
          ),
        });
      }
      return { effects, rejections: [] };
    }

    case 'recarga.registrada': {
      const p = event.payload;
      // The same top-up twice is one: applied again, it drew the balance and filled the drawer twice.
      if (await read.recarga(p.recargaId)) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'Esa recarga ya fue registrada' }] };
      }

      const sesion = await read.sesionCaja(p.sesionId);
      if (!sesion) {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'No hay una caja abierta' }] };
      }
      if (sesion.estado === 'cerrada') {
        return { effects: [], rejections: [{ eventId: event.id, reason: 'La caja ya fue cerrada' }] };
      }

      const id = saldoId(p.operadora);
      const actual = await read.saldoTelefonico(id);
      const facial = centavos(p.montoFacial);

      /**
       * With no recorded balance to draw against, the cost is taken as face and the commission
       * is zero — the shop clearly earned something, but there is no honest basis on record for
       * saying how much. The balance goes negative so it shows up rather than silently
       * inventing margin. Same principle as negative stock: never block the sale, make the gap
       * visible. Credit that cost nothing is a basis of nothing: what is sold of it is all
       * commission, where it used to cost its face and take the balance below zero (2026-10-20).
       */
      const enCola = p.formaPago !== 'efectivo_cordobas';
      const hayBase = actual && actual.facial > 0 && actual.costo >= 0;
      const costoProporcional = hayBase
        ? mulDiv(actual.costo, facial, actual.facial)
        : facial;
      const comision = subMoney(facial, costoProporcional);

      const effects: Effect[] = [
        {
          kind: 'saldo.upsert',
          id,
          row: {
            id,
            operadora: p.operadora,
            facial: subMoney(actual?.facial ?? ZERO, facial),
            costo: subMoney(actual?.costo ?? ZERO, costoProporcional),
            rev,
          },
        },
        {
          kind: 'recarga.upsert',
          id: p.recargaId,
          row: {
            id: p.recargaId,
            sesionId: p.sesionId,
            operadora: p.operadora,
            telefono: p.telefono,
            montoFacial: facial,
            costo: costoProporcional,
            comision,
            formaPago: p.formaPago,
            fecha: p.fecha,
            estadoPago: enCola ? 'pendiente' : 'confirmado',
            referencia: p.referencia ?? null,
            rev,
          },
        },
        {
          kind: 'asiento',
          entry: entry(event.id, p.fecha, `Recarga ${p.operadora} ${p.telefono}`, [
            debit(enCola ? 'TRANSFERENCIAS_PENDIENTES' : 'CAJA_CORDOBAS', facial, p.referencia ?? ''),
            credit('SALDO_TELEFONICO', costoProporcional),
            ...(comision > 0 ? [credit('COMISIONES', comision)] : []),
            // Credit that cost more than it sells for: a commission below nothing, on the books as one.
            ...(comision < 0 ? [debit('COMISIONES', centavos(-comision), 'Costó más de lo que se cobró')] : []),
          ]),
        },
      ];

      // Cash taken in still has to reconcile at the corte, even though it is not a sale.
      if (p.formaPago === 'efectivo_cordobas') {
        effects.push({
          kind: 'sesion.patch',
          id: p.sesionId,
          rev,
          patch: { efectivoCordobas: addMoney(sesion.efectivoCordobas, facial) },
        });
      } else {
        effects.push({
          kind: 'sesion.patch',
          id: p.sesionId,
          rev,
          patch: { otrosPagos: addMoney(sesion.otrosPagos, facial) },
        });
      }

      return { effects, rejections: [] };
    }

    default:
      return null;
  }
}

/** A top-up's transfer or wallet payment, seen arriving (`pago.confirmado` with `recargaId`). */
export async function confirmarPagoRecarga(
  eventId: string,
  recargaId: string,
  referencia: string | undefined,
  read: Reader,
  rev: number,
  /** The day it was seen at the bank or the wallet (`diaDeLaConfirmacion`). */
  diaVisto: string,
  /** The córdoba account a transfer was seen in (`bancos.ts`); absent, «Bancos». */
  cuenta?: CuentaBanco,
): Promise<ApplyResult> {
  const r = await read.recarga(recargaId);
  if (!r) return { effects: [], rejections: [{ eventId, reason: 'Esa recarga no existe' }] };
  if (r.estadoPago !== 'pendiente') return { effects: [], rejections: [{ eventId, reason: 'Ese pago ya fue resuelto' }] };
  const ref = referencia ?? r.referencia;
  const banco = r.formaPago === 'transferencia' ? cuentaDeTransferencia(cuenta) : null;
  const destino = banco ?? CUENTA_CONFIRMADA[r.formaPago] ?? 'BANCOS';
  const fecha = diaDeLaConfirmacion(await read.config(), destino, r.fecha, diaVisto);
  return {
    effects: [
      {
        kind: 'recarga.upsert',
        id: r.id,
        row: { ...r, estadoPago: 'confirmado', referencia: ref, ...(banco && cuenta ? { cuenta: banco } : {}), rev },
      },
      {
        kind: 'asiento',
        entry: entry(eventId, fecha, `Recarga ${r.operadora} ${r.telefono}: pago confirmado`, [
          debit(destino, r.montoFacial, ref ?? ''),
          credit('TRANSFERENCIAS_PENDIENTES', r.montoFacial),
        ]),
      },
    ],
    rejections: [],
  };
}

/** A top-up's payment that never arrived: the credit was sent, so the money is a loss to chase. */
export async function rechazarPagoRecarga(
  eventId: string,
  recargaId: string,
  motivo: string,
  read: Reader,
  rev: number,
): Promise<ApplyResult> {
  const r = await read.recarga(recargaId);
  if (!r) return { effects: [], rejections: [{ eventId, reason: 'Esa recarga no existe' }] };
  if (r.estadoPago !== 'pendiente') return { effects: [], rejections: [{ eventId, reason: 'Ese pago ya fue resuelto' }] };
  return {
    effects: [
      { kind: 'recarga.upsert', id: r.id, row: { ...r, estadoPago: 'rechazado', rev } },
      {
        kind: 'asiento',
        entry: entry(eventId, r.fecha, `Recarga ${r.operadora} ${r.telefono}: pago no cobrado: ${motivo}`, [
          debit('TRANSFERENCIAS_PERDIDAS', r.montoFacial, motivo),
          credit('TRANSFERENCIAS_PENDIENTES', r.montoFacial),
        ]),
      },
    ],
    rejections: [],
  };
}

export type { Centavos };
